import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { trpc } from '~/api/trpc';
import LocalDateTime from '~/lib/localdatetime';
import { AppointmentStatus } from '~/types/server-types/AppointmentStatus';

export type AppointmentStep = {
    id: string;
    name: string;
    shortDescription: string;
    minuteStart: number;
    minuteEnd: number;
    treatmentColor: string;
    roomCategoryConstraints: Array<{ roomCategory: { id: string; name: string } }>;
    equipmentCategoryConstraints: Array<{ equipmentCategory: { id: string; name: string } }>;
    practitionerCategoryConstraints: Array<{ practitionerCategory: { id: string; name: string } }>;
};

export type AppointmentCustomer = {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phone: string | null;
    city?: string;
    zipCode?: string;
    country: string;
    street?: string;
    streetNumber?: string;
    preferredCommunicationType: string;
    category: string;
    alternativePhone?: string;
    note?: string;
};

export type TimeSlot = {
    from: string;
    to: string;
    steps: {
        from: string;
        to: string;
        practitioners: {
            id: string;
            firstName: string;
            lastName: string;
        }[];
        rooms: {
            id: string;
            name: string;
        }[];
    }[];
};

export type SimplePractitioner = {
    id: number;
    name: string;
    categoryId: number;
    categoryName: string;
    isAvailable?: boolean; // Practitioner is available during this step's time window.
    isShiftAvailable?: boolean; // Practitioner SHIFT is available during this step's time window -- this means it does NOT take appointments into account during this timeframe.
    numberOfExistingBookings?: number; // Practitioner number of existing bookings during this step's time window.
    canPerform?: boolean; // Practitioner is able to deliver the service for this step.
}

export type SimpleRoom = {
    id: number;
    name: string;
    categoryId: number;
    categoryName: string;
    equipment: {
        id: number;
        name: string;
    }[];
    numberOfExistingBookings?: number; // Room number of existing bookings during this step's time window.
    isAvailable?: boolean; // Room is available during this step's time window.
}

export type SimpleEquipment = {
    id: number;
    name: string;
    categoryId: number;
    categoryName: string;
    numberOfExistingBookings?: number; // Equipment number of existing bookings during this step's time window.
    isAvailable?: boolean; // Equipment is available during this step's time window.
    isShiftAvailable?: boolean; // Equipment SHIFT is available during this step's time window -- this means it does NOT take appointments into account during this timeframe.
}

export type Step = {
    id: string;
    name: string;
    shortDescription: string;
    minuteStart: number;
    minuteEnd: number;
    treatmentColor: string;
    treatmentVariantId: string | null;
    originalTreatmentStepId: string | null;
    // NEW: Link step to bookable item
    bookableItemId: string | null;

    practitioners: SimplePractitioner[];
    rooms: SimpleRoom[];
    equipment: SimpleEquipment[];

    initialPractitioners: SimplePractitioner[];
    initialRooms: SimpleRoom[];
    initialEquipment: SimpleEquipment[];

    selectablePractitioners: SimplePractitioner[];
    selectableRooms: SimpleRoom[];
    selectableEquipment: SimpleEquipment[];

    acceptablePractitioners: {
        categoryId: number;
        practitionerIds?: number[];
    }[] | null; // Null = no preference
    acceptableRooms: {
        categoryId: number;
        roomIds?: number[];
    }[] | null; // Null = no preference
    acceptableEquipments: {
        categoryId: number;
        equipmentIds?: number[];
    }[] | null; // Null = no preference
};

export type Customer = {
    id: number;
    firstName: string;
    lastName: string;
    email: string | null;
    birthday: string | null;
    phone: string | null;
    city?: string | null;
    zipCode?: string | null;
    country: string;
    street?: string | null;
    streetNumber?: string | null;
    preferredCommunicationType: string;
    category: string;
    alternativePhone?: string | null;
    note?: string | null;
    praxismanagerId?: number | null;
};

// Bookable item type for staff selection
// Supports both 'bookable_item' (from CMS) and 'legacy' (old appointments with only treatmentVariantId)
export type SelectedBookableItem = {
    // Discriminant: 'bookable_item' for CMS items, 'legacy' for old variant-only appointments
    type: 'bookable_item' | 'legacy';

    // Bookable item info
    // For legacy: bookableItemId is a generated UUID (for UI keying/removal), bookableItemType is 'SERVICE'
    bookableItemId: string;
    bookableItemType: 'SERVICE' | 'BUNDLE';
    bookableItemName: string;
    color: string;
    
    // Selected variant
    treatmentVariantId: string;
    treatmentVariantName: string;
    priceCents: number;
    
    // For BUNDLE type
    bundleId?: string;
    bundleName?: string;
    bundleUnitQuantity?: number;
    bundleUnitPriceGross?: number;

    // When true, this item redeems an existing bundle voucher instead of purchasing the bundle.
    isBundleRedemption?: boolean;
    
    // For SERVICE type: add-ons
    addOns: {
        addOnId: string;
        addOnName: string;
        variantId: string;
        variantName: string;
        priceCents: number;
    }[];
    
    // Step IDs created from this bookable item
    stepIds: string[];
};

type AppointmentStateData = {
    color: string;
    appointmentId: string | null;
    mode: 'create' | 'modify';
    customer: Customer | null;
    messageFromCustomer: string;
    messageToCustomer: string;
    appointmentNotes: string;
    notifyCustomer: boolean;
    vouchers: string;
    status: AppointmentStatus | null;
    type: string;
    influencerType: string | null;
    steps: Step[];
    colorIndex: number;
    selectedBookableItems: SelectedBookableItem[];
    showAvailableTimes: boolean;
    dateRange: {
        from: LocalDateTime;
        to: LocalDateTime;
    };
    dragPreview: {
        id: string;
        minuteStart: number;
        minuteEnd: number;
        duration: number;
        visible: boolean;
    } | null;
    treatmentOpen: boolean;
    treatmentValue: string;
    treatmentSearch: string;
    selectedTimeSlot: TimeSlot | null;
    startDate: LocalDateTime;
    startTime: string;
};

type AppointmentStateActions = {
    setColor: (color: string) => void;
    setAppointmentId: (appointmentId: string | null) => void;
    setMode: (mode: 'create' | 'modify') => void;
    setCustomer: (customer: Customer | null) => void;
    setMessageFromCustomer: (messageFromCustomer: string) => void;
    setMessageToCustomer: (messageToCustomer: string) => void;
    setAppointmentNotes: (appointmentNotes: string) => void;
    setNotifyCustomer: (notifyCustomer: boolean) => void;
    setVouchers: (vouchers: string) => void;
    setStatus: (status: AppointmentStatus) => void;
    setType: (type: string) => void;
    setInfluencerType: (influencerType: string | null) => void;
    setSteps: (steps: Step[]) => void;
    setColorIndex: (index: number) => void;
    setSelectedBookableItems: (items: SelectedBookableItem[]) => void;
    addBookableItem: (item: SelectedBookableItem) => void;
    removeBookableItem: (bookableItemId: string) => void;
    setShowAvailableTimes: (show: boolean) => void;
    setDateRange: (range: { from: LocalDateTime; to: LocalDateTime }) => void;
    setDragPreview: (preview: {
        id: string;
        minuteStart: number;
        minuteEnd: number;
        duration: number;
        visible: boolean;
    } | null) => void;
    setTreatmentOpen: (open: boolean) => void;
    setTreatmentValue: (value: string) => void;
    setTreatmentSearch: (search: string) => void;
    setSelectedTimeSlot: (slot: TimeSlot | null) => void;
    setStartDate: (date: LocalDateTime) => void;
    setStartTime: (time: string) => void;
    refreshSelectableResources: (storeId: number, timezone: string, trpcUtils: ReturnType<typeof trpc.useUtils>) => Promise<void>;
    reset: () => void;
};

type FullAppointmentState = AppointmentStateData & AppointmentStateActions;

const initialDataState: AppointmentStateData = {
    color: "#bdd7a5",
    appointmentId: null,
    mode: 'create',
    customer: null,

    messageFromCustomer: '',
    messageToCustomer: '',
    appointmentNotes: '',
    notifyCustomer: false,

    vouchers: '',
    status: AppointmentStatus.SCHEDULED,
    type: 'NORMAL',
    influencerType: null,
    steps: [],
    colorIndex: 0,
    selectedBookableItems: [],
    showAvailableTimes: false,
    dateRange: {
        from: LocalDateTime.fromUTCDate(new Date(), "Europe/Berlin"),
        to: LocalDateTime.fromUTCDate(new Date(), "Europe/Berlin").addDays(1),
    },
    dragPreview: null,
    treatmentOpen: false,
    treatmentValue: '',
    treatmentSearch: '',
    selectedTimeSlot: null,
    startDate: LocalDateTime.fromUTCDate(new Date(), 'Europe/Berlin'),
    startTime: '09:00',
};

export const useCreateOrModifyAppointmentStore = create<FullAppointmentState>()(
    devtools(
        (set, get) => ({
            ...initialDataState,

            setColor: (color) => set({ color }),
            setAppointmentId: (appointmentId) => set({ appointmentId }),
            setMode: (mode) => set({ mode, notifyCustomer: mode === 'create' }),
            setCustomer: (customer) => set({ customer }),
            setMessageFromCustomer: (messageFromCustomer) => set({ messageFromCustomer }),
            setMessageToCustomer: (messageToCustomer) => set({ messageToCustomer }),
            setAppointmentNotes: (appointmentNotes) => set({ appointmentNotes }),
            setNotifyCustomer: (notifyCustomer) => set({ notifyCustomer }),
            setVouchers: (vouchers) => set({ vouchers }),
            setStatus: (status) => set({ status }),
            setType: (type) => set({ type }),
            setInfluencerType: (influencerType) => set({ influencerType }),
            setSteps: (steps) => set({ steps }),
            setColorIndex: (index) => set({ colorIndex: index }),
            setSelectedBookableItems: (items) => set({ selectedBookableItems: items }),
            addBookableItem: (item) => set((state) => ({ 
                selectedBookableItems: [...state.selectedBookableItems, item] 
            })),
            removeBookableItem: (bookableItemId) => set((state) => {
                const itemToRemove = state.selectedBookableItems.find(i => i.bookableItemId === bookableItemId);
                const stepIdsToRemove = itemToRemove?.stepIds ?? [];
                return {
                    selectedBookableItems: state.selectedBookableItems.filter(i => i.bookableItemId !== bookableItemId),
                    steps: state.steps.filter(s => !stepIdsToRemove.includes(s.id)),
                };
            }),
            setShowAvailableTimes: (show) => set({ showAvailableTimes: show }),
            setDateRange: (range) => set({ dateRange: range }),
            setDragPreview: (preview) => set({ dragPreview: preview }),
            setTreatmentOpen: (open) => set({ treatmentOpen: open }),
            setTreatmentValue: (value) => set({ treatmentValue: value }),
            setTreatmentSearch: (search) => set({ treatmentSearch: search }),
            setSelectedTimeSlot: (slot) => set({ selectedTimeSlot: slot }),
            setStartDate: (date) => set({ startDate: date }),
            setStartTime: (time) => set({ startTime: time }),
            refreshSelectableResources: async (storeId: number, timezone: string, trpcUtils: ReturnType<typeof trpc.useUtils>) => {
                const currentState = get();
                const startDate = currentState.startDate;
                const startTime = currentState.startTime;
                const steps = currentState.steps;

                console.log("refreshSelectableResources", startTime);

                if (steps.length === 0) {
                    return;
                }

                const startDateStr = startDate.format("yyyy-MM-dd");
                const startTimeStr = startTime; // add seconds
                const appointmentStartTime = new LocalDateTime(`${startDateStr} ${startTimeStr}`, 'Europe/Berlin');
                console.log("AppointmentStartTime: " + appointmentStartTime.getDisplayDateTime());

                const stepsSelectableResources = await trpcUtils.appointments.getResourcesConstraints.fetch({
                    storeId: storeId === -1 ? 'digital' : storeId,
                    startTime: appointmentStartTime.toJson(),
                    steps: steps.map((step) => ({
                        originalTreatmentStepId: step.originalTreatmentStepId,
                        originalTreatmentVariantId: step.treatmentVariantId,
                        name: step.name,
                        shortDescription: step.shortDescription,
                        startMinute: step.minuteStart,
                        endMinute: step.minuteEnd,
                        duration: step.minuteEnd - step.minuteStart,
                        acceptablePractitioners: step.acceptablePractitioners,
                        acceptableRooms: step.acceptableRooms,
                        acceptableEquipments: step.acceptableEquipments,
                    })),
                    existingAppointmentId: currentState.mode === 'modify' ? currentState.appointmentId ?? undefined : undefined,
                });

                console.log("Steps Selectable Resources for " + appointmentStartTime.getDisplayDateTime());
                console.log(stepsSelectableResources);

                const newState = get();
                if (newState.steps.length !== currentState.steps.length) {
                    console.log("Not setting selectable resources because steps changed during request");
                    return;
                }
                set({
                    steps: steps.sort((a, b) => a.minuteStart - b.minuteStart).map((step, idx) => ({
                        // stepsSelectableResources is in the same order as the steps.
                        ...step,
                        selectablePractitioners: stepsSelectableResources[idx].practitioners,
                        selectableRooms: stepsSelectableResources[idx].rooms,
                        selectableEquipment: stepsSelectableResources[idx].equipments,
                    }))
                })
            },

            reset: () => set({
                ...initialDataState,
                dateRange: {
                    from: LocalDateTime.fromUTCDate(new Date(), "Europe/Berlin"),
                    to: LocalDateTime.fromUTCDate(new Date(), "Europe/Berlin").addDays(1),
                },
                startDate: LocalDateTime.fromUTCDate(new Date(), 'Europe/Berlin'),
            }),
        }),
        { name: 'CreateOrModifyAppointmentStore' }
    )
);